<template>
   <section v-if="slide" class="fc-hero">
      <div class="fc-hero__glow" aria-hidden="true"></div>
      <div class="fc-hero__bands" aria-hidden="true"></div>
      <div class="container-1440 fc-hero__inner">
         <div class="fc-hero__content">
            <!-- G2 rating - static snapshot (4.4/5 from g2.com/products/fusioncharts),
                 not fetched live since G2 has no public API. Needs a manual check
                 whenever marketing refreshes it.
                 TODO(content): no Contentful field for this yet - hardcoded here,
                 same as the Resources-menu quote in SimpleDropdown.vue. -->
            <div class="fc-hero__proof">
               <svg
                  class="fc-hero__stars"
                  viewBox="0 0 100 20"
                  role="img"
                  aria-label="Rated 4.4 out of 5 on G2"
               >
                  <defs>
                     <linearGradient id="fc-hero-star44" x1="0" x2="1" y1="0" y2="0">
                        <stop offset="40%" stop-color="#f5a623" />
                        <stop offset="40%" stop-color="rgba(255,255,255,.26)" />
                     </linearGradient>
                  </defs>
                  <g transform="scale(.8333)">
                     <path
                        fill="#f5a623"
                        d="M12 2.6l2.9 6 6.5.9-4.7 4.5 1.1 6.4L12 17.3l-5.8 3.1 1.1-6.4-4.7-4.5 6.5-.9z"
                     />
                     <path
                        fill="#f5a623"
                        transform="translate(24)"
                        d="M12 2.6l2.9 6 6.5.9-4.7 4.5 1.1 6.4L12 17.3l-5.8 3.1 1.1-6.4-4.7-4.5 6.5-.9z"
                     />
                     <path
                        fill="#f5a623"
                        transform="translate(48)"
                        d="M12 2.6l2.9 6 6.5.9-4.7 4.5 1.1 6.4L12 17.3l-5.8 3.1 1.1-6.4-4.7-4.5 6.5-.9z"
                     />
                     <path
                        fill="#f5a623"
                        transform="translate(72)"
                        d="M12 2.6l2.9 6 6.5.9-4.7 4.5 1.1 6.4L12 17.3l-5.8 3.1 1.1-6.4-4.7-4.5 6.5-.9z"
                     />
                     <path
                        fill="url(#fc-hero-star44)"
                        transform="translate(96)"
                        d="M12 2.6l2.9 6 6.5.9-4.7 4.5 1.1 6.4L12 17.3l-5.8 3.1 1.1-6.4-4.7-4.5 6.5-.9z"
                     />
                  </g>
               </svg>
               <span
                  ><b>4.4 / 5</b> on
                  <a
                     href="https://www.g2.com/products/fusioncharts/reviews"
                     target="_blank"
                     rel="noopener"
                     >G2</a
                  >, with
                  <span class="fc-hero__proof-users"><b>100k+ users</b> worldwide</span></span
               >
            </div>

            <h1 class="fc-hero__title" v-html="slide.header"></h1>
            <p
               v-if="slide.shortDescription"
               class="fc-hero__desc"
               v-html="slide.shortDescription"
            ></p>

            <div class="fc-hero__npm">
               <code>npm i fusioncharts</code>
               <button
                  type="button"
                  class="fc-hero__npm-copy"
                  :class="{ 'is-copied': npmCopied }"
                  :aria-label="npmCopied ? 'Copied' : 'Copy install command'"
                  @click="copyInstallCommand"
               >
                  <svg
                     v-if="!npmCopied"
                     viewBox="0 0 24 24"
                     fill="none"
                     stroke="currentColor"
                     stroke-width="2"
                     stroke-linejoin="round"
                  >
                     <rect x="9" y="9" width="12" height="12" rx="2" />
                     <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                  </svg>
                  <svg
                     v-else
                     viewBox="0 0 24 24"
                     fill="none"
                     stroke="currentColor"
                     stroke-width="2"
                     stroke-linecap="round"
                     stroke-linejoin="round"
                  >
                     <path d="M20 6L9 17l-5-5" />
                  </svg>
               </button>
            </div>

            <div v-if="slide.ctas && slide.ctas.length" class="fc-hero__cta">
               <a
                  v-for="(cta, index) in slide.ctas"
                  :key="index"
                  :href="cta.link"
                  class="fc-pill-btn"
                  :class="index === 0 ? 'fc-pill-btn--light' : 'fc-pill-btn--violet'"
                  @click.prevent="goTo(cta.link)"
                  >{{ cta.header }}</a
               >
            </div>
         </div>

         <div class="fc-hero__card">
            <div class="fc-hero__card-bar"></div>
            <div class="fc-hero__card-head">
               <span class="fc-hero__card-title">Revenue breakdown</span>
               <span class="fc-badge-pill fc-hero__card-tag">Interactive</span>
            </div>

            <div
               :id="chartId"
               ref="heroChart"
               class="fc-hero__chart-wrap"
               role="img"
               aria-label="Revenue, expenses and profit chart by quarter"
            ></div>

            <div class="fc-hero__controls">
               <div class="fc-hero__legend" role="group" aria-label="Toggle series">
                  <button
                     v-for="item in legendItems"
                     :key="item.key"
                     type="button"
                     class="fc-hero__legend-item"
                     :class="{ 'is-off': !seriesVisible[item.key] }"
                     :aria-pressed="seriesVisible[item.key]"
                     :aria-label="`Toggle ${item.key} series`"
                     @click="toggleSeries(item.key)"
                  >
                     <span
                        class="fc-hero__legend-swatch"
                        :style="{ background: item.color }"
                     ></span>
                     {{ item.key }}
                  </button>
               </div>

               <div class="fc-hero__toggle" role="tablist" aria-label="Chart view">
                  <button
                     v-for="view in views"
                     :key="view.key"
                     type="button"
                     role="tab"
                     :aria-selected="activeView === view.key"
                     :class="{ 'is-active': activeView === view.key }"
                     @click="activeView = view.key"
                  >
                     {{ view.label }}
                  </button>
               </div>
            </div>

            <div class="fc-hero__kpis">
               <div class="fc-hero__kpi">
                  <b>$4.82M</b>
                  <span>Total revenue</span>
                  <i>&uarr; 18.4%</i>
               </div>
               <div class="fc-hero__kpi">
                  <b>$1.96M</b>
                  <span>Net profit</span>
                  <i>&uarr; 22.1%</i>
               </div>
               <div class="fc-hero__kpi">
                  <b>$2.86M</b>
                  <span>Expenses</span>
                  <i class="fc-hero__kpi-down">&uarr; 9.3%</i>
               </div>
            </div>
         </div>
      </div>
   </section>
</template>

<script>
const CATEGORIES = ["Q1 '25", "Q2 '25", "Q3 '25", "Q4 '25", "Q1 '26", "Q2 '26", "Q3 '26"];
const SERIES = [
   { key: 'Revenue', color: '#f0653c', data: [420, 510, 480, 615, 590, 730, 805] },
   { key: 'Expenses', color: '#5b46f0', data: [275, 320, 300, 375, 350, 420, 455] },
   { key: 'Profit', color: '#20c9a5', data: [145, 190, 180, 240, 240, 310, 350] },
];
// Real chart types behind the view toggle - a plain type swap since they
// all share the same categories/dataset JSON shape.
const VIEW_CHART_TYPE = {
   grouped: 'mscolumn2d',
   stacked: 'stackedcolumn2d',
   horizontal: 'msbar2d',
   line: 'msline',
};

export default {
   name: 'HeroBannerNew',
   props: {
      config: {
         type: Object,
         default: () => ({ slides: [] }),
      },
   },
   data() {
      return {
         npmCopied: false,
         activeView: 'grouped',
         views: [
            { key: 'grouped', label: 'Grouped' },
            { key: 'stacked', label: 'Stacked' },
            { key: 'horizontal', label: 'Horizontal' },
            { key: 'line', label: 'Line' },
         ],
         seriesVisible: { Revenue: true, Expenses: true, Profit: true },
         chartId: `fc-hero-chart-${Math.random()
            .toString(36)
            .slice(2)}`,
         chartInstance: null,
      };
   },
   computed: {
      slide() {
         return this.config && this.config.slides && this.config.slides[0];
      },
      legendItems() {
         return SERIES.map(s => ({ key: s.key, color: s.color }));
      },
   },
   watch: {
      activeView() {
         this.updateHeroChart();
      },
   },
   mounted() {
      this.$nextTick(() => this.renderHeroChart());
   },
   beforeDestroy() {
      if (this.chartInstance) {
         try {
            this.chartInstance.dispose();
         } catch (e) {
            /* chart already torn down */
         }
      }
   },
   methods: {
      toggleSeries(key) {
         const nextVisible = { ...this.seriesVisible, [key]: !this.seriesVisible[key] };
         if (Object.values(nextVisible).some(Boolean)) {
            this.seriesVisible = nextVisible;
            this.updateHeroChart();
         }
      },
      // Hero CTAs open in the same tab - deliberately ignores the CMS
      // isTargetBlank flag (GitHub is the only link that should leave the
      // current tab, and $openReload already handles that case).
      goTo(url) {
         if (this.$reloadType(url)) {
            this.$openReload(url);
         } else if (url.charAt(0) === '#') {
            const el = document.querySelector(url);
            if (el) el.scrollIntoView({ behavior: 'smooth' });
         } else {
            this.$router.push(url).catch(() => {});
         }
      },
      copyInstallCommand() {
         if (!navigator.clipboard) return;
         navigator.clipboard
            .writeText('npm i fusioncharts')
            .then(() => {
               this.npmCopied = true;
               setTimeout(() => {
                  this.npmCopied = false;
               }, 1500);
            })
            .catch(() => {});
      },
      buildDataSource() {
         const visibleSeries = SERIES.filter(s => this.seriesVisible[s.key]);
         return {
            chart: {
               // 'candy' specifically - it's the only theme asset loaded
               // globally (index.template.html), and naming anything else
               // (e.g. 'fusion') silently falls back to FusionCharts' built-in
               // default look: glossy gradient bars on a white canvas, which
               // fights every color/background override below. Skipping this
               // key entirely (as before) *also* falls back to that default -
               // the theme file being loaded on the page doesn't opt a chart
               // into it by itself. That default lacks the candy theme's own
               // hover extensions (the per-category highlight band behind the
               // hovered cluster, and the combined multi-series tooltip),
               // which is why those never appeared here even though the rest
               // of this config matches a chart elsewhere on the site that
               // has them.
               theme: 'candy',
               //
               // bgAlpha/canvasBgAlpha at '0' still rendered an opaque white
               // canvas in this FusionCharts build, so instead of relying on
               // alpha compositing, paint the chart+canvas the same solid
               // color as the card behind it - visually identical to
               // "transparent over dark", without depending on that alpha
               // behaving.
               bgColor: '151A26',
               bgAlpha: '100',
               canvasBgColor: '151A26',
               canvasBgAlpha: '100',
               showBorder: '0',
               showCanvasBorder: '0',
               showShadow: '0',
               showPlotBorder: '0',
               usePlotGradientColor: '0',
               // Subtle "backlight" glow on the hovered bar - plotHighlightEffect
               // (an easy name to guess) is actually a legend-hover-only feature
               // internally and does nothing here since showLegend is '0'; a
               // direct per-bar hover effect is plotBorderHover*/plotFillHover*.
               plotBorderHoverColor: 'FFFFFF',
               plotBorderHoverAlpha: '55',
               plotBorderHoverThickness: '2',
               divLineColor: 'FFFFFF',
               divLineAlpha: '12',
               divLineThickness: '1',
               numDivLines: '4',
               showAlternateHGridColor: '0',
               axisLineAlpha: '0',
               yAxisValueFontColor: '9AA0B4',
               yAxisValueFontSize: '11',
               labelFontColor: 'C8CCDA',
               labelFontSize: '11',
               showValues: '0',
               showLegend: '0',
               numberPrefix: '$',
               // Raw data values are already in thousands (e.g. 805 means
               // $805K) - defaultNumberScale tells FusionCharts that, so its
               // own auto-scaling crosses over to "M"/"B" at the right
               // boundary instead of what a literal numberSuffix:'K' did:
               // double up with FusionCharts' own auto-abbreviation once an
               // axis value hit exactly 1000, rendering "$1KK".
               formatNumberScale: '1',
               defaultNumberScale: 'K',
               numberScaleValue: '1000,1000',
               numberScaleUnit: 'M,B',
               // 1, not 0 - the Stacked view's summed totals can land on a
               // K-value that isn't a clean multiple of 1000 once scaled to
               // M (e.g. 1500K -> 1.5M). At 0 decimals that rounds up to
               // "$2M", colliding with the axis's actual $2M gridline and
               // showing it twice. FusionCharts drops the trailing zero on
               // whole numbers on its own, so this doesn't turn clean
               // values like $2M into "$2.0M".
               decimals: '1',
               plotToolText: '<b>$seriesName</b> $label: $dataValue',
               animation: '1',
               exportEnabled: '0',
            },
            categories: [{ category: CATEGORIES.map(label => ({ label })) }],
            dataset: visibleSeries.map(s => ({
               seriesname: s.key,
               color: s.color.replace('#', ''),
               data: s.data.map(value => ({ value })),
            })),
         };
      },
      renderHeroChart() {
         FusionCharts.ready(() => {
            this.chartInstance = new FusionCharts({
               type: VIEW_CHART_TYPE[this.activeView],
               renderAt: this.chartId,
               width: '100%',
               height: '100%',
               dataFormat: 'json',
               dataSource: this.buildDataSource(),
            });
            this.chartInstance.render();
         });
      },
      // View-toggle/legend clicks update the existing chart instance in
      // place (chartType + setJSONData) instead of dispose()-ing and
      // re-rendering a fresh one - a full re-render briefly repaints
      // FusionCharts' own default (white) canvas before our dark styling
      // re-applies, which showed up as a white flash on every click.
      updateHeroChart() {
         if (!this.chartInstance) {
            this.renderHeroChart();
            return;
         }
         this.chartInstance.chartType(VIEW_CHART_TYPE[this.activeView]);
         this.chartInstance.setJSONData(this.buildDataSource());
      },
   },
};
</script>

<style lang="scss" scoped>
.fc-hero {
   position: relative;
   overflow: hidden;
   color: #fff;
   background: linear-gradient(126deg, #2a1160 0%, #3f1e8a 34%, #6733bd 68%, #9350da 100%);

   &__glow {
      position: absolute;
      inset: 0;
      pointer-events: none;
      background: radial-gradient(46% 58% at 78% 10%, rgba(196, 146, 255, 0.34), transparent 70%),
         radial-gradient(42% 52% at 6% 88%, rgba(38, 14, 96, 0.72), transparent 72%),
         radial-gradient(34% 44% at 98% 92%, rgba(221, 168, 255, 0.24), transparent 70%);
   }

   &__bands {
      position: absolute;
      inset: 0;
      pointer-events: none;
      background: linear-gradient(
            118deg,
            transparent 45%,
            rgba(255, 255, 255, 0.055) 45%,
            rgba(255, 255, 255, 0.055) 57%,
            transparent 57%
         ),
         linear-gradient(
            118deg,
            transparent 64%,
            rgba(255, 255, 255, 0.032) 64%,
            rgba(255, 255, 255, 0.032) 71%,
            transparent 71%
         );
   }

   &__inner {
      position: relative;
      z-index: 1;
      display: grid;
      grid-template-columns: 1.05fr 0.95fr;
      gap: 56px;
      align-items: center;
      padding: 64px 54px 64px;
   }

   &__proof {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 6px 14px 6px 12px;
      border-radius: 999px;
      font-size: 15px;
      color: rgba(255, 255, 255, 0.85);

      b {
         color: #fff;
      }
      a {
         color: inherit;

         &:hover {
            color: #fff;
            text-decoration: underline;
            text-underline-offset: 3px;
         }
      }
      // Keeps "100k+ users worldwide" together as one unit when the line
      // wraps on narrow screens, instead of splitting mid-phrase and
      // leaving "worldwide" stranded alone on its own line.
      &-users {
         white-space: nowrap;
      }
   }
   &__stars {
      width: 96px;
      height: 19px;
      flex: 0 0 auto;
   }

   &__title {
      font-size: clamp(34px, 4.9vw, 60px);
      letter-spacing: -0.038em;
      line-height: 1.06;
      // basefontBold, not a synthetic 800 - the site's webfont (Source Sans
      // Pro) only has a real weight-700 face loaded, so 800 had no matching
      // face to render and the browser was faking a weak synthetic bold.
      font-family: basefontBold, 'Helvetica Neue', Arial, sans-serif;
      font-weight: 700;
      color: #fff;
      margin: 0;

      // Content note: wrap "charts and maps." in this class in the CMS
      // header HTML so the phrase moves to the next line as one unit
      // instead of an arbitrary mid-phrase break.
      ::v-deep .nowrap {
         white-space: nowrap;

         @media (max-width: 480px) {
            white-space: normal;
         }
      }
      ::v-deep .hl-wrap {
         position: relative;
         display: inline-block;
      }
      ::v-deep .bolt {
         position: absolute;
         right: -17px;
         top: 6px;
         font-size: 32px;
         line-height: 1;
         pointer-events: none;
         transform: rotate(12deg);
         font-family: 'Apple Color Emoji', 'Segoe UI Emoji', 'Noto Color Emoji', sans-serif;

         // font-size above only sizes an emoji/text glyph - once the CMS
         // content swaps that for an <img>/<svg> (renders consistently
         // across browsers, unlike emoji), give it its own explicit size.
         img,
         svg {
            display: block;
            width: 28px;
            height: 28px;
         }
      }
      ::v-deep .hl {
         display: inline-block;
         background: #6b34c0;
         font-style: italic;
         padding: 1px 17px 4px 12px;
         margin-top: 6px;
         box-decoration-break: clone;
         -webkit-box-decoration-break: clone;
      }
      // The actual CMS content nests .bolt directly inside a single
      // .banner-faster-wrap span rather than the mockup's separate
      // .hl-wrap > .hl split - so this one class needs to do both jobs:
      // the positioning context for .bolt (like .hl-wrap) AND the
      // highlight box styling (like .hl).
      ::v-deep .banner-faster-wrap {
         position: relative;
         display: inline-block;
         background: #6b34c0;
         font-style: italic;
         padding: 1px 17px 4px 6px;
         margin-top: 6px;
         box-decoration-break: clone;
         -webkit-box-decoration-break: clone;
      }
   }

   &__desc {
      margin: 24px 0 0;
      font-size: 17px;
      line-height: 1.62;
      color: rgba(255, 255, 255, 0.78);
      max-width: 520px;

      // Same fix as the headline/KPIs: the inherited font-family only has a
      // real weight-700 face under the basefontBold name, not under
      // whatever this text inherits, so a plain <b>/<strong> from the CMS
      // content had no matching face to request "bolder" from and rendered
      // barely different from the surrounding text. Also bumped to full
      // opacity (unlike the rest of this text) so the emphasized bits stand
      // out against the dimmer body copy around them.
      ::v-deep b,
      ::v-deep strong {
         font-family: basefontBold, 'Helvetica Neue', Arial, sans-serif;
         font-weight: 700;
         color: #fff;
      }
   }

   &__npm {
      display: inline-flex;
      align-items: center;
      gap: 10px;
      margin-top: 26px;
      padding: 8px 10px 8px 15px;
      border-radius: 8px;
      background: rgba(0, 0, 0, 0.3);
      border: 1px solid rgba(255, 255, 255, 0.14);

      code {
         font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
         font-size: 14px;
         color: #e8eaf4;
      }
   }
   &__npm-copy {
      display: grid;
      place-items: center;
      width: 28px;
      height: 28px;
      border: none;
      border-radius: 6px;
      background: transparent;
      color: rgba(255, 255, 255, 0.6);
      cursor: pointer;
      transition: background 0.13s ease, color 0.13s ease;

      &:hover {
         background: rgba(255, 255, 255, 0.12);
         color: #fff;
      }
      &.is-copied {
         color: #20c9a5;
      }
      svg {
         width: 15px;
         height: 15px;
      }
   }

   &__cta {
      display: flex;
      gap: 12px;
      flex-wrap: wrap;
      margin-top: 30px;
   }

   /* -------------------- chart card -------------------- */
   &__card {
      background: #151a26;
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: 16px;
      box-shadow: 0 30px 70px -24px rgba(11, 11, 40, 0.35);
      overflow: hidden;
   }
   &__card-bar {
      height: 3px;
      background: linear-gradient(90deg, #f0653c, #b45cf0 55%, #20c9a5);
   }
   &__card-head {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 12px 16px;
      border-bottom: 1px solid rgba(255, 255, 255, 0.07);
   }
   &__card-title {
      font-size: 15px;
      font-weight: 700;
   }
   &__card-tag {
      position: relative;
      isolation: isolate;
      overflow: hidden;
      color: #f0865c;
      background: rgba(240, 134, 92, 0.12);
      border: 1px solid rgba(240, 134, 92, 0.45);
      text-transform: uppercase;
      font-size: 10px;

      // One slow diagonal pass of light every few seconds, sitting behind
      // the label - matches the reference's .hero-card__tag exactly.
      &::after {
         content: '';
         position: absolute;
         z-index: -1;
         top: -60%;
         bottom: -60%;
         left: -55%;
         width: 34%;
         transform: skewX(-18deg);
         background: linear-gradient(100deg, transparent, rgba(255, 214, 196, 0.5), transparent);
         animation: fc-hero-tag-shine 3.5s ease-in-out infinite;
      }
      @media (prefers-reduced-motion: reduce) {
         &::after {
            animation: none;
         }
      }
   }
   @keyframes fc-hero-tag-shine {
      0% {
         left: -55%;
      }
      31% {
         left: 135%;
      }
      100% {
         left: 135%;
      }
   }

   &__chart-wrap {
      height: 238px;
      padding: 12px 12px 0;

      // Same FusionCharts quirk as the homepage chart showcase
      // (ChartShowcase.vue): the library always stamps a literal white
      // background-color on the <svg> node itself, independent of the
      // bgColor/canvasBgColor theme config above - that JSON only paints
      // the <rect> drawn inside the svg, not the svg element's own CSS
      // background. chartType()/setJSONData() rebuild that svg internally
      // even though we never dispose()/re-render, so the raw white flashes
      // through for a frame before the new rect paints over it. Force it
      // transparent so the card's dark background shows through instead.
      ::v-deep svg {
         background-color: transparent !important;
      }
   }

   &__controls {
      display: flex;
      align-items: center;
      justify-content: space-between;
      flex-wrap: wrap;
      gap: 8px;
      padding: 2px 12px 11px;
   }

   &__legend {
      display: flex;
      flex-wrap: wrap;
      gap: 14px;
   }
   &__legend-item {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      border: none;
      background: transparent;
      padding: 0;
      font-size: 11.5px;
      color: #9aa0b4;
      cursor: pointer;
      transition: opacity 0.15s ease;

      &:hover {
         opacity: 0.8;
      }
      &.is-off {
         opacity: 0.35;
      }
      &:focus-visible {
         outline: 1px solid rgba(255, 255, 255, 0.6);
         outline-offset: 2px;
      }
   }
   &__legend-swatch {
      width: 10px;
      height: 10px;
      border-radius: 2px;
      flex: 0 0 auto;
   }

   &__toggle {
      display: flex;
      flex-wrap: wrap;
      justify-content: flex-end;
      gap: 6px;

      button {
         padding: 4px 10px;
         border-radius: 99px;
         border: 1px solid rgba(255, 255, 255, 0.12);
         background: transparent;
         font-size: 11px;
         font-weight: 600;
         color: rgba(255, 255, 255, 0.5);
         cursor: pointer;
         transition: background 0.14s ease, color 0.14s ease, border-color 0.14s ease;

         &:hover {
            color: #fff;
            border-color: rgba(255, 255, 255, 0.26);
         }
         &.is-active {
            background: rgba(240, 101, 60, 0.15);
            border-color: rgba(240, 101, 60, 0.45);
            color: #f0865c;
         }
      }
   }

   &__kpis {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 1px;
      background: rgba(255, 255, 255, 0.07);
      border-top: 1px solid rgba(255, 255, 255, 0.07);
   }
   &__kpi {
      background: #151a26;
      padding: 11px 10px;
      text-align: center;

      b {
         display: block;
         // Same bug as the headline: this font-family only has a real
         // weight-700 face loaded, so an 800 request here had no matching
         // face and the browser fell all the way back to a system serif
         // instead of faking a bold sans - hence "wrong font, has serifs".
         font-family: basefontBold, 'Helvetica Neue', Arial, sans-serif;
         font-size: 17px;
         font-weight: 700;
         letter-spacing: -0.02em;
      }
      span {
         display: block;
         font-size: 11px;
         color: rgba(255, 255, 255, 0.5);
         margin-top: 1px;
      }
      i {
         font-style: normal;
         // public/ds-local.css has a global `i { font-family: fusion-icon
         // !important }` reset for icon-font tags, which also hijacks this
         // plain semantic <i>. Needs !important to win against that.
         font-family: inherit !important;
         font-size: 15px;
         font-weight: 700;
         color: var(--fc-revamp-teal);
      }
   }
   &__kpi-down {
      color: #f0865c !important;
   }

   @media (max-width: 1100px) {
      &__inner {
         grid-template-columns: 1fr;
         gap: 40px;
         padding: 56px 24px 64px;
      }
   }
   @media (max-width: 760px) {
      &__inner {
         padding: 48px 20px 56px;
         gap: 34px;
      }
      &__title {
         font-size: clamp(30px, 7.6vw, 44px);
      }
      &__desc {
         margin-top: 20px;
         font-size: 15px;
      }
      &__proof {
         font-size: 14px;
      }
   }
   @media (max-width: 480px) {
      &__inner {
         padding: 36px 16px 44px;
      }
      &__cta {
         gap: 10px;

         .fc-pill-btn {
            flex: 1 1 auto;
         }
      }
      &__npm {
         width: 100%;
         justify-content: space-between;
      }
      &__chart-wrap {
         height: 206px;
      }
      &__controls {
         padding: 8px 10px 10px;
      }
      &__toggle {
         gap: 5px;

         button {
            padding: 4px 9px;
            font-size: 11px;
         }
      }
   }
}
</style>
